import { FINISH } from "@/config/designer";
import type { VoxelField } from "@/core/field/VoxelField";
import type { ExtractedMesh } from "@/core/mesh/surfaceNets";
import { pointIsGloss, type GlossDab } from "./types";

/** Add deterministic fine-sand relief to non-painted faces, inward only. */
export function applyGripTexture(
  mesh: ExtractedMesh,
  field: VoxelField,
  glossDabs: readonly GlossDab[],
  amplitude = FINISH.gripAmplitudeMm,
): ExtractedMesh {
  if (amplitude <= 0 || mesh.positions.length === 0) return mesh;
  const positions = new Float32Array(mesh.positions);
  const normals = vertexNormals(mesh);
  const baseEpsilon = Math.max(1e-5, field.h * 0.12);
  const anchors = extentAnchors(mesh.positions);

  for (let v = 0; v < positions.length / 3; v++) {
    const p = v * 3;
    const x = positions[p] as number;
    const y = positions[p + 1] as number;
    const z = positions[p + 2] as number;
    if (y <= baseEpsilon || pointIsGloss(x, y, z, glossDabs)) continue;
    const grain = 0.22 + hashNoise(x * 2.7, y * 3.1, z * 2.9) * 0.78;
    const depth = amplitude * grain;
    positions[p] = x - (normals[p] as number) * depth;
    positions[p + 1] = Math.max(0, y - (normals[p + 1] as number) * depth);
    positions[p + 2] = z - (normals[p + 2] as number) * depth;
  }
  // One untouched point on each extent retains exact advertised dimensions;
  // the remainder of that face still receives the grip relief.
  for (const vertex of anchors) {
    if (vertex < 0) continue;
    const p = vertex * 3;
    positions[p] = mesh.positions[p] as number;
    positions[p + 1] = mesh.positions[p + 1] as number;
    positions[p + 2] = mesh.positions[p + 2] as number;
  }
  return { ...mesh, positions };
}

function extentAnchors(positions: Float32Array): number[] {
  const values = [Infinity, -Infinity, Infinity, -Infinity, Infinity, -Infinity];
  const indices = [-1, -1, -1, -1, -1, -1];
  for (let v = 0; v < positions.length / 3; v++) {
    const p = v * 3;
    for (let axis = 0; axis < 3; axis++) {
      const value = positions[p + axis] as number;
      const low = axis * 2;
      if (value < (values[low] as number)) {
        values[low] = value;
        indices[low] = v;
      }
      if (value > (values[low + 1] as number)) {
        values[low + 1] = value;
        indices[low + 1] = v;
      }
    }
  }
  return indices;
}

function vertexNormals(mesh: ExtractedMesh): Float32Array {
  const normals = new Float32Array(mesh.positions.length);
  const { positions, indices } = mesh;
  for (let t = 0; t < indices.length; t += 3) {
    const ai = (indices[t] as number) * 3;
    const bi = (indices[t + 1] as number) * 3;
    const ci = (indices[t + 2] as number) * 3;
    const ux = (positions[bi] as number) - (positions[ai] as number);
    const uy = (positions[bi + 1] as number) - (positions[ai + 1] as number);
    const uz = (positions[bi + 2] as number) - (positions[ai + 2] as number);
    const vx = (positions[ci] as number) - (positions[ai] as number);
    const vy = (positions[ci + 1] as number) - (positions[ai + 1] as number);
    const vz = (positions[ci + 2] as number) - (positions[ai + 2] as number);
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    for (const p of [ai, bi, ci]) {
      normals[p] = (normals[p] as number) + nx;
      normals[p + 1] = (normals[p + 1] as number) + ny;
      normals[p + 2] = (normals[p + 2] as number) + nz;
    }
  }
  for (let p = 0; p < normals.length; p += 3) {
    const length = Math.hypot(
      normals[p] as number,
      normals[p + 1] as number,
      normals[p + 2] as number,
    ) || 1;
    normals[p] = (normals[p] as number) / length;
    normals[p + 1] = (normals[p + 1] as number) / length;
    normals[p + 2] = (normals[p + 2] as number) / length;
  }
  return normals;
}

function hashNoise(x: number, y: number, z: number): number {
  const value = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
  return value - Math.floor(value);
}
